import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  OfferService,
  PrintService,
  ProfileService,
  StyleService,
  TemplateService,
  LogService,
  AuthService,
} from '../../services';
import { Campaign, KnownReward } from '../../models';
import {
  MainTemplate,
  Multiplier,
  TemplateSetting,
  UcVersion,
} from '../../models/template';
import {
  Subject,
  fromEvent,
  take,
  takeUntil,
  firstValueFrom,
  tap,
  switchMap,
  catchError,
  combineLatest,
  of,
} from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackbarComponent } from '../snackbar/snackbar.component';
import { RedemptionButtonState } from '../../models/redemption-template.model';
import { InteractionType } from '../../models/interaction';
import { LogSource, LogType } from '../../models/common';
import { ChooseRewardItem } from '../../models/chooseReward';

@Component({
  selector: 'app-offer-modal',
  templateUrl: './offer-modal.component.html',
  styleUrls: ['./offer-modal.component.scss'],
})
export class OfferModalComponent implements OnInit, OnDestroy {
  public isLoaded = false;
  public mysteryConfirmed = false;
  public isRiskedReward = false;
  public rewardRiskWinningTemplate = false;
  public riskEmptyRewardTemplate = false;
  public mainTemplate!: MainTemplate;
  public isInteraction = InteractionType.None;
  public offer: Campaign;
  public reward: KnownReward;
  public oldReward: KnownReward;
  public newReward: KnownReward;
  public qrCode = '';
  public tab: TemplateSetting;
  public redemptionButtonState = RedemptionButtonState;
  public isOfferPrinted: boolean = false;
  public isLoading: boolean = false;

  public isSwapEnabled: boolean = false;
  public selectSwapId: string | undefined;
  public swapPage: number = 1;
  public isSwapConfirmed: boolean = false;
  public isNeedToUpdate: boolean = false;

  public accessRequestId: string;
  private lobbyToken: string;
  private indexesOffer: [number, number];
  private isGameResultTranseffered = false;
  private readonly closeListener$ = new Subject();
  private cardOfferDetails: any;
  private mysteryOffer: any;
  private riskOffer: any;

  public printOnScreen: boolean = false;
  public useNewVersion: boolean = false;
  public useNewVersionKiosks: string = "";

  public multiplierButtonClick$: Subject<any> = new Subject();
  public rewardPicked = false;

  constructor(
    public readonly styleService: StyleService,
    private readonly offerService: OfferService,
    private readonly dialogRef: MatDialogRef<any>,
    private readonly printService: PrintService,
    private readonly profileService: ProfileService,
    private readonly templateService: TemplateService,
    private readonly logService: LogService,
    private readonly authService: AuthService,
    private snackbar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      chooseRewardsList: ChooseRewardItem[];
      indexesOffer: [number, number];
      tab: TemplateSetting;
      offer: Campaign;
      accessRequestId: string;
      lobbyToken: string;
      reward: KnownReward;
      isNeedToReedemOrUpdateBalance: boolean;
    }
  ) {
    this.indexesOffer = data.indexesOffer;
    this.tab = data.tab;
    this.offer = data.offer;
    this.accessRequestId = data.accessRequestId;
    this.lobbyToken = data.lobbyToken;
    this.reward = data.reward;
    this.oldReward = {} as KnownReward;
    this.newReward = {} as KnownReward;
    this.qrCode = this.reward?.rewardCode || '';

    const gameInteractionType = this.offerService.gameInteractionType(
      this.offer
    );

    if (
      data.isNeedToReedemOrUpdateBalance ||
      gameInteractionType === InteractionType.GameInteraction
    ) {
      this.isNeedToUpdate = true;
    }

    if (
      gameInteractionType !== InteractionType.GameInteraction &&
      data.isNeedToReedemOrUpdateBalance
    ) {
      this.runCustomProperties();

      if (
        !this.reward?.customProperties?.executeUcWebhookOn ||
        this.reward?.customProperties?.executeUcWebhookOn ===
          RedemptionButtonState.Issue
      ) {
        this.offerService.updatePlayerBalanceByRewardValue(this.reward);
      }
    }
  }

  ngOnInit(): void {
    this.cardOfferDetails = this.tab.configurations.cardOfferDetails;
    this.mysteryOffer = this.tab.configurations.mysteryOffer;
    this.riskOffer = this.tab.configurations.riskOffer;
    this.isLoaded = false;

    this.templateService
      .templateData$()
      .pipe(take(1))
      .subscribe((template) => {
        this.isInteraction = this.offerService.gameInteractionType(this.offer);
        if (this.isInteraction === InteractionType.GameInteraction) {
          this.logService.writeLog(
            `Started game`,
            LogType.DEBUG,
            LogSource.MODAL
          );
          this.subscribeToOfferEvents();
        }
        this.isLoaded = true;
        this.mainTemplate = template;
      });
    this.offerService.setDefaultCustomProperties(this.reward);

    this.printOnScreen = this.data.tab.configurations?.voucher?.printOnScreen?.isTestMode;
    this.useNewVersion = this.data.tab.configurations?.voucher?.advPrint?.isEnabled;
    this.useNewVersionKiosks = this.data.tab.configurations?.voucher?.advPrint?.kioskDeviceIds;

    this.printService.isNewPrintVersion = this.useNewVersion && 
      (
        this.useNewVersionKiosks.split(",").findIndex((dev) => dev.trim() === this.authService.deviceId) > -1 || 
        !this.useNewVersionKiosks
      )

    this.multiplierButtonClick$
      .pipe(
        switchMap((params: any) => {
          const {
            id,
            earnStart,
            earnEnd,
            claimStart,
            claimEnd,
            rate,
            rewardSource,
            accountType,
          } = params;

          let rateString: string = rate.toString() + '.0';

          return combineLatest(
            this.profileService.universalConnector.claimMultiplier(
              id,
              earnStart,
              earnEnd,
              claimStart,
              claimEnd,
              rateString,
              JSON.stringify(rewardSource),
              accountType
            ),
            of(params)
          );
        }),
        tap((res: any) => {
          const data = res[0];
          const params = res[1];

          const newObject = {
            accountType: params.accountType,
            claimDate: '',
            claimStart: params.claimStart,
            claimEnd: params.claimEnd,
            depositAmount: '',
            depositDate: '',
            earnEnd: params.earnEnd,
            earnStart: params.earnStart,
            id: this.reward.campaignRewardItemId as string,
            maturityDate: '',
            rate: params.rate.toString(),
            rewardSource: JSON.stringify(params.source),
            state: 'Claimed',
          };

          this.logService.writeLog(`Multiplier initiated for ${this.reward.campaignRewardItemId}`, LogType.DEBUG, LogSource.MULTIPLIER);

          if (data.errors.length > 0 || data.message !== 'Success') {
            this.snackbar.openFromComponent(SnackbarComponent, {
              panelClass: 'error',
              duration: 5000,
              horizontalPosition: 'end',
              data: {
                type: 'error',
                message: data.message,
              },
            });
          } else {
            this.profileService.multiplier.next([
              ...this.profileService.multiplier.getValue(),
              newObject,
            ]);

            this.snackbar.openFromComponent(SnackbarComponent, {
              panelClass: 'success',
              duration: 5000,
              horizontalPosition: 'end',
              data: {
                type: 'success',
                message: 'Multiplier successfully claimed',
              },
            });

            this.isNeedToUpdate = true;
            this.closeModal();
          }

          this.printService.setIsPrinting(false, 'Multiplier click');
        }),
        catchError((err: any) => {
          const { error } = err;

          if (error.errors) {
            this.snackbar.openFromComponent(SnackbarComponent, {
              panelClass: 'error',
              duration: 5000,
              horizontalPosition: 'end',
              data: {
                type: 'error',
                message:
                  'This campaign is not configured correctly. Please check your settings.',
              },
            });
          }

          this.logService.writeLog(`Multiplier error for ${this.reward.campaignRewardItemId}`, LogType.ERROR, LogSource.MULTIPLIER);
          this.printService.setIsPrinting(false, 'Multiplier click');
          return of(null);
        })
      )
      .subscribe();

    // close dialog when click outside it
    this.dialogRef.backdropClick().subscribe(() => {
      this.isNeedToUpdate = true;
      this.closeModal(true);
    });
  }

  private runCustomProperties() {
    if (this.reward?.customProperties?.printOnIssue) {
      this.logService.writeLog(
        'Print called from constructor gameInteraction',
        LogType.INFO,
        LogSource.MODAL
      );
      this.checkPrintPage();
    } else if (this.reward?.customProperties?.redeemOnIssue) {
      this.logService.writeLog(
        'Redeem called from constructor gameInteraction',
        LogType.INFO,
        LogSource.MODAL
      );
      this.printService.setIsPrinting(true, 'constructor gameInteraction');
      firstValueFrom(this.offerService.onRedeem(this.reward, this.dialogRef, this.offer.title));

      if (this.reward.rate > 0 && this.hasNotBeenClaimed()) {
        firstValueFrom(this.profileService.universalConnector.claimMultiplier(
          this.reward?.campaignRewardItemId as string,
          this.data.offer.customProperties?.earnPeriodStartDate,
          this.data.offer.customProperties?.earnPeriodEndDate, 
          this.data.offer.formattedStartDate,
          this.data.offer.formattedEndDate,
          this.reward.rate.toString() + ".0",
          JSON.stringify(this.data.offer.customProperties?.multiplierRewardSource),
          this.reward.costType
        ))
        this.closeModal();
      }
    }
  }

  public showRewardSection(event: any) {
    this.rewardPicked = true;
    this.reward = event.reward;
    this.data.offer.knownReward = this.reward;

    this.runCustomProperties();

    if (event.closeModal) {
      this.closeModal();
    }
  }

  public get offerQr(): string {
    return this.reward.rewardPosCode?.length
      ? this.reward.rewardPosCode
      : this.qrCode;
  }

  public confirmMystery() {
    this.offerService.claimMysteryReward(this.reward.rewardCode).subscribe(
      () => {
        this.mysteryConfirmed = true;
      },
      () => {
        this.snackbar.openFromComponent(SnackbarComponent, {
          panelClass: 'error',
          duration: 3000,
          horizontalPosition: 'end',
          data: {
            type: 'error',
            message: 'Reward confirm failed. Please try again',
          },
        });
      }
    );
  }

  public confirmMysteryOk() {
    this.reward.isMystery = false;
  }

  public riskReward() {
    this.isRiskedReward = true;
  }

  public keepRisk() {
    this.offerService.keepReward(this.reward.rewardCode).subscribe(
      (resp) => {
        this.reward = resp;
        this.data.offer.knownReward = resp;
        this.isNeedToUpdate = true;
      },
      () => {
        this.snackbar.openFromComponent(SnackbarComponent, {
          panelClass: 'error',
          duration: 3000,
          horizontalPosition: 'end',
          data: {
            type: 'error',
            message: 'Reward keep failed. Please try again',
          },
        });
      }
    );
  }

  public okRisk() {
    this.reward = this.newReward;
    this.data.offer.knownReward = this.reward;
    this.isNeedToUpdate = true;
  }

  public okEmptyRisk() {
    this.isNeedToUpdate = true;
    this.closeModal();
  }

  public confirmRisk() {
    this.oldReward = this.reward;
    if (this.reward.campaignRewardItemId) {
      this.offerService
        .riskReward(this.reward.rewardCode, this.reward.campaignRewardItemId)
        .subscribe(
          (resp) => {
            this.newReward = resp;
            this.data.offer.knownReward = resp;
            this.rewardRiskWinningTemplate = true;
            if (this.newReward.isEmpty) {
              this.riskEmptyRewardTemplate = true;
            } else {
              this.riskEmptyRewardTemplate = false;
            }

            this.runCustomProperties();
          },
          () => {
            this.snackbar.openFromComponent(SnackbarComponent, {
              panelClass: 'error',
              duration: 3000,
              horizontalPosition: 'end',
              data: {
                type: 'error',
                message: 'Reward risk failed. Please try again',
              },
            });
          }
        );
    }
  }

  public printPage() {
    let isPrintProcessing = localStorage.getItem('isPrintProcessing');

    if (isPrintProcessing) return;

    localStorage.setItem('isPrintProcessing', 'true');

    const [pageIndex, templateIndex] = this.indexesOffer;
    this.printService.setIsPrinting(true, 'Offer Modal printPage');
    this.snackbar.open(`${this.offerService.loadingPrintMessage}`, '', {
      panelClass: 'success',
      duration: 3000,
      horizontalPosition: 'end',
    });
    this.isNeedToUpdate = true;

    const rewardToUse =
      !this.isSwapConfirmed && !this.isRiskedReward
        ? this.offer.knownReward
        : this.newReward;

    const page: string = this.printService.preparePage(
      this.tab.configurations.voucher.html,
      {
        pageIndex,
        templateIndex,
        voucher: rewardToUse.rewardPosCode?.length
          ? rewardToUse.rewardPosCode
          : this.qrCode,
        patronName: this.profileService.fallBackDataPointValues().playerName,
        memberNumber: rewardToUse.externalId,
        promotionName: this.offer.title,
        promotionDescription: this.offer.description,
        rewardDescription: rewardToUse.description,
        expiresDate: rewardToUse.expirationDate,
        termsAndConditions: this.offer.termsAndConditions,
        rewardName: rewardToUse.name,
      },
      'html',
      rewardToUse.campaignRewardItemId
    );

    if (this.isRiskedReward || this.isSwapConfirmed) {
      this.isRiskedReward = false;
      this.isSwapConfirmed = false;
      this.newReward = {} as KnownReward;
    }

    this.logService.writeLog(
      `Start printing offer ${JSON.stringify(this.offer)} ${
        this.offer?.knownReward
      }`,
      LogType.INFO,
      LogSource.PRINT
    );

    return (this.printService.isNewPrintVersion ? this.printService.printPage_V2(page) : this.printService.printPage(page)).then(() => {
      this.closeModal();
      this.isLoading = false;
      const customProps = rewardToUse?.customProperties;

      this.isOfferPrinted = true;
      this.snackbar.open(`${this.offerService.successPrintMessage}`, '', {
        panelClass: 'success',
        duration: 5000,
        horizontalPosition: 'end',
      });

      localStorage.removeItem('isPrintProcessing');

      setTimeout(() => {
        this.printService.setIsPrinting(
          false,
          'Offer Modal printPage timeout'
        );
      }, this.offerService.timeoutPeriod * 1000);

      if (
        !(
          this.isInteraction !== InteractionType.GameInteraction &&
          rewardToUse?.customProperties?.redeemOnIssue
        ) &&
        (customProps?.redeemOnIssue ||
          customProps?.redeemOnPrint ||
          customProps?.showRedeemButton)
      ) {
        firstValueFrom(
          this.offerService.onRedeem(
            rewardToUse,
            !!this.tab.templateConfig.isNeedToCloseAfterPrint
              ? this.dialogRef
              : null,
            this.offer.title
          )
        );
      }
    })
    .catch(() => {
      this.printFailed();
    });
  }

  public hasNotBeenClaimed(reward?: KnownReward): boolean {
    return (
      (this.reward ?? reward).rate > 0 &&
      !this.profileService.multiplier
        .getValue()
        .some(
          (multiplier: Multiplier) =>
            multiplier.id === (this.reward ?? reward).campaignRewardItemId
        )
    );
  }

  public claimMultiplier(
    id: string,
    earnStart: string,
    earnEnd: string,
    claimStart: string,
    claimEnd: string,
    rate: number,
    rewardSource: string,
    accountType: string
  ): void {
    if (this.profileService.ucVersion === UcVersion.Version_2 && rate > 0) {
      this.printService.setIsPrinting(true, 'Multiplier click');
      this.multiplierButtonClick$.next({
        id,
        earnStart,
        earnEnd,
        claimStart,
        claimEnd,
        rate,
        rewardSource,
        accountType,
      });
    }
  }

public rewardRedemptionButtonState(): string {

  const customProps = this.reward?.customProperties;

  if (customProps?.showRedeemButton) return RedemptionButtonState.Redeem;
  if (customProps?.showPrintButton)  return RedemptionButtonState.Print;

  return RedemptionButtonState.None;
}


  public get printRedeemButtonStyles() {
    if (this.rewardRedemptionButtonState() === RedemptionButtonState.Redeem) {
      return this.isRewardRedeemed()
        ? this.riskOffer?.buttonPrintRedeemOfferInactive
        : this.riskOffer?.buttonRedeemRisk;
    }
    return this.isOfferPrinted
      ? this.cardOfferDetails?.buttonPrintRedeemOfferInactive
      : this.cardOfferDetails?.redeemOfferButton;
  }

  //  --- NEW getters ---
// Styles/text for the new Risk Redeem button
get buttonRedeemRiskStyles() {
  return this.riskOffer?.buttonRedeemRisk;
}
get riskRedeemButtonTitle() {
  return this.riskOffer?.buttonRedeemRiskText?.trim() || 'Redeem';
}

public executeRiskRedeem(): void {
  this.printService.setIsPrinting(true, "Risk Redeem button");
  firstValueFrom(this.offerService.onRedeem(this.reward, this.dialogRef, this.data.offer.title));
}

get shouldShowRiskRedeem(): boolean {
  return this.reward?.customProperties?.showRedeemButton === true;
}

get shouldShowPrimaryAction(): boolean {
  return !this.shouldShowRiskRedeem &&
  this.rewardRedemptionButtonState() !== this.redemptionButtonState.None;
}

get shouldShowFallbackPrint(): boolean {
  const customProp = this.reward?.customProperties;
  return !this.shouldShowRiskRedeem &&
  this.rewardRedemptionButtonState() === this.redemptionButtonState.None && (customProp?.showPrintButton !== false);
}

  get breakValueButtonTitle() {
    return this.cardOfferDetails?.buttonBreakValueText?.trim() ?? 'Break Value';
  }

  get printRedeemButtonTitle() {
    if (this.rewardRedemptionButtonState() === RedemptionButtonState.Redeem) {
      return this.isRewardRedeemed()
        ? this.cardOfferDetails?.buttonRedeemedOfferText?.trim() || 'Redeemed'
        : this.cardOfferDetails?.buttonRedeemOfferText?.trim() || 'Redeem';
    }
    return this.printButtonText;
  }

  get mysteryButtonTitle() {
    return this.mysteryOffer?.buttonClaimMysteryPrizeText
      ? this.mysteryOffer?.buttonClaimMysteryPrizeText?.trim()
      : 'Claim Mystery Prize';
  }

  get buttonClaimMysteryPrizeStyles() {
    return this.mysteryOffer?.buttonClaimMysteryPrize;
  }

  get riskButtonTitle() {
    return this.riskOffer?.buttonRiskText
      ? this.riskOffer?.buttonRiskText?.trim()
      : 'Risk this offer';
  }

  get riskPrintButtonTitle() {
    if (this.rewardRedemptionButtonState() === RedemptionButtonState.Redeem) {
      return this.riskOffer?.buttonRiskRedeemText?.trim() || 'Redeem offer';
    }

    return this.riskOffer?.buttonRiskPrintText?.trim() || 'Print offer';
  }

  get shouldShowPrintOrRedeem(): boolean {
  return this.reward?.customProperties?.showRedeemButton === true;
}
  get redeemRiskRedeemButtonTitle() {
    return this.riskOffer?.buttonRiskRedeemText
      ? this.riskOffer?.buttonRiskRedeemText?.trim()
      : 'Redeem';
  }
  get buttonClaimRiskStyles() {
    return this.riskOffer?.buttonRisk;
  }

  get buttonClaimRiskPrintStyles() {
    return this.riskOffer?.buttonRiskPrint;
  }

  get buttonClaimRiskRedeemStyles() {
    return this.riskOffer?.buttonRiskRedeem;
  }

  get mysteryOkButtonTitle() {
    return this.mysteryOffer?.buttonOkClaimMysteryPrizeText
      ? this.mysteryOffer?.buttonOkClaimMysteryPrizeText?.trim()
      : 'OK';
  }

  get buttonOkClaimMysteryPrizeStyles() {
    return this.mysteryOffer?.buttonOkClaimMysteryPrize;
  }

  get buttonConfirmRiskStyles() {
    return this.riskOffer?.buttonConfirmRisk;
  }
  get buttonConfirmRiskTitle() {
    return this.riskOffer?.buttonConfirmRiskText
      ? this.riskOffer?.buttonConfirmRiskText?.trim()
      : 'Risk Offer';
  }

  get buttonKeepRiskStyles() {
    return this.riskOffer?.buttonKeepRisk;
  }

  get buttonKeepRiskTitle() {
    return this.riskOffer?.buttonKeepRiskText
      ? this.riskOffer?.buttonKeepRiskText?.trim()
      : 'Keep Offer';
  }

  get buttonKeepRiskOkStyles() {
    return this.riskOffer?.buttonKeepRiskOk;
  }

  get buttonEmptyRiskOkStyles() {
    return this.riskOffer?.buttonEmptyRisk;
  }

  get buttonEmptyRiskOkTitle() {
    return this.riskOffer?.buttonEmptyRiskText
      ? this.riskOffer?.buttonEmptyRiskText?.trim()
      : 'Ok';
  }

  get buttonKeepRiskOkTitle() {
    return this.riskOffer?.buttonKeepRiskOkText
      ? this.riskOffer?.buttonKeepRiskOkText?.trim()
      : 'Ok';
  }

  get buttonMultiplierText() {
    return this.hasNotBeenClaimed()
      ? this.cardOfferDetails?.buttonMultiplierText ?? 'Multiplier'
      : this.cardOfferDetails?.buttonMultiplierClaimedText ?? 'Claimed';
  }

  public get printButtonText() {
    return this.isOfferPrinted
      ? this.cardOfferDetails?.buttonPrintedOfferText?.trim() || 'Printed'
      : this.cardOfferDetails?.buttonPrintOfferText?.trim() || 'Print';
  }

  public get isPrinting() {
    return this.printService.isPrinting;
  }

  public getIframeUrl(): string {
    return this.offerService.getGameInteractionUrl(
      this.lobbyToken,
      this.accessRequestId,
      this.offer
    );
  }

  public closeModal(disablePrinting: boolean = false): void {
    if (disablePrinting) {
      this.printService.setIsPrinting(false, 'Offer Modal closeModal');
    }

    this.logService.writeLog(
      `Close modal with isNeedToUpdate: ${this.isNeedToUpdate}`,
      LogType.DEBUG,
      LogSource.MODAL
    );
    this.dialogRef.close(this.isNeedToUpdate);
  }

  public isRewardRedeemed(): boolean {
    if (!this.reward?.rewardStates) {
      return false;
    }
    return this.reward?.rewardStates?.includes('Redeemed');
  }

  public executeRewardAction() {
    if (!this.reward.rewardCode && !this.reward.rewardPosCode) return;

    const customProps = this.reward?.customProperties;

    if (customProps?.printOnIssue || customProps?.showPrintButton) {
      this.logService.writeLog(
        'Print called from executeRewardAction',
        LogType.INFO,
        LogSource.PRINT
      );
      this.checkPrintPage();
    } else {
      this.logService.writeLog(
        'Redeem called from constructor executeRewardAction',
        LogType.INFO,
        LogSource.REDEEM
      );
      this.printService.setIsPrinting(true, 'Offer Modal closeModal');
      firstValueFrom(this.offerService.onRedeem(this.reward, this.dialogRef, this.data.offer.title));
    }
  }

  private printFailed(): void {
    this.isLoading = false;
    this.printService.setIsPrinting(false, 'Offer Modal printFailed');
    localStorage.removeItem('isPrintProcessing');
    this.snackbar.openFromComponent(SnackbarComponent, {
      panelClass: 'error',
      duration: 5000,
      horizontalPosition: 'end',
      data: {
        type: 'error',
        message: 'Print failed, Please try again.',
      },
    });
  }

  public checkPrintPage(): void {
    this.logService.writeLog(
      'checkPrintPage called',
      LogType.INFO,
      LogSource.MODAL
    );
    try {
      this.printPage();
    } catch (error) {
      this.isLoading = false;
      this.printFailed();
    }
  }

  private subscribeToOfferEvents() {
    const self = this;

    fromEvent(window, 'message')
      .pipe(takeUntil(this.closeListener$))
      .subscribe(async (event: any) => {
        let eventData: KnownReward;

        if (this.isGameResultTranseffered) return;

        try {
          eventData = JSON.parse(event.data);
        } catch (error) {
          return;
        }

        if (eventData.customProperties?.['printOnIssue']) {
          self.updateOffer(false, true);
        }

        const rewardCode =
          eventData?.rewardCode ||
          (eventData as any)?.combinedCode?.auxCodeValue ||
          (eventData as any)?.combinedCode?.codeValue;

        if (!this.isGameResultTranseffered && eventData && rewardCode) {
          this.offerService.setDefaultCustomProperties(event);
          this.isLoading = true;
          this.isGameResultTranseffered = true;
          this.isNeedToUpdate = true;

          this.logService.writeLog(
            `Game completed`,
            LogType.DEBUG,
            LogSource.MODAL
          );

          if (eventData.customProperties?.['redeemOnIssue']) {
            this.offerService.onRedeem(eventData, null, this.data.offer.title).subscribe({
              next: (respReward) => {
                if (respReward) {
                  self.updateOffer(true, false);

                  if (eventData.rate > 0 && this.hasNotBeenClaimed(eventData)) {
                    firstValueFrom(this.profileService.universalConnector.claimMultiplier(
                      respReward?.campaignRewardItemId as string,
                      this.data.offer.customProperties?.earnPeriodStartDate,
                      this.data.offer.customProperties?.earnPeriodEndDate, 
                      this.data.offer.formattedStartDate,
                      this.data.offer.formattedEndDate,
                      eventData.rate.toString() + ".0",
                      JSON.stringify(this.data.offer.customProperties?.multiplierRewardSource),
                      eventData.costType
                    ))
                    this.closeModal();
                  }
                }
              },
            });
          } else {
            this.closeModal(true);
          }
        }
      });

    if (
      this.isInteraction === InteractionType.None &&
      this.reward?.customProperties?.['printOnIssue']
    ) {
      this.logService.writeLog(
        'Print called from subscribeToOfferEvents',
        LogType.INFO,
        LogSource.GAME
      );
      this.checkPrintPage();
    }
  }

  public get swapSlides(): KnownReward[] {
    const swaps = [...this.reward?.swaps?.rewards];
    const pageItems = 4 * this.swapPage;

    return swaps.splice(pageItems - 4, pageItems);
  }

  public changeSwapPage(isNext: boolean): void {
    let page = this.swapPage;

    const totalRewards = this.reward?.swaps?.rewards?.length;
    const totalPages = Math.ceil(totalRewards / 4);

    if (isNext) {
      if (this.swapPage < totalPages) {
        page = this.swapPage + 1;
      } else {
        page = 1;
      }
    } else {
      if (this.swapPage > 1) {
        page = this.swapPage - 1;
      } else {
        page = totalPages;
      }
    }

    this.swapPage = page;
  }

  public selectSwap(): void {
    this.isLoading = true;

    const rewardCode =
      this.reward?.rewardCode ||
      this.reward?.combinedCode?.auxCodeValue ||
      this.reward?.combinedCode?.codeValue;

    this.offerService
      .swapReward(this.selectSwapId as string, rewardCode)
      .pipe(take(1))
      .subscribe((reward: any) => {
        this.isSwapEnabled = true;
        this.selectSwapId = undefined;
        this.swapPage = 1;
        this.isSwapConfirmed = true;
        this.isLoading = false;
        this.isNeedToUpdate = true;
        this.newReward = reward;

        if (reward?.customProperties?.['printOnIssue']) {
          this.logService.writeLog(
            'Print called from swap',
            LogType.INFO,
            LogSource.SWAP_OFFER
          );
          this.checkPrintPage();
        }

        if (reward?.customProperties?.['redeemOnIssue']) {
          this.offerService.onRedeem(reward, null, this.data.offer.title).subscribe(() => {
            this.isLoading = false;

            if (reward.rate > 0 && this.hasNotBeenClaimed(reward)) {
              firstValueFrom(this.profileService.universalConnector.claimMultiplier(
                reward?.campaignRewardItemId as string,
                this.data.offer.customProperties?.earnPeriodStartDate,
                this.data.offer.customProperties?.earnPeriodEndDate, 
                this.data.offer.formattedStartDate,
                this.data.offer.formattedEndDate,
                reward.rate.toString() + ".0",
                JSON.stringify(this.data.offer.customProperties?.multiplierRewardSource),
                reward.costType
              ))
            }

            this.closeModal();
          });
        }

        setTimeout(() => {
          this.isSwapEnabled = false;
          this.reward = this.newReward;
        }, 2000);
      });
  }

  private updateOffer(
    needToCheckInteraction: boolean,
    tryToPrint: boolean
  ): void {
    this.offerService
      .getCampaigns(this.tab)
      .pipe(take(1))
      .subscribe({
        next: (couponBody) => {
          const offer = couponBody.campaigns.find(
            (x) => x.id === this.offer.id
          );

          if (offer) {
            this.offer = offer;
            this.reward = offer.knownReward;
            this.qrCode = this.reward?.rewardCode || '';

            if (tryToPrint) {
              this.logService.writeLog(
                'Print called from updateOffer',
                LogType.INFO,
                LogSource.OFFER
              );
              this.checkPrintPage();
            }

            if (needToCheckInteraction) {
              this.isInteraction = this.offerService.gameInteractionType(
                this.offer
              );
              this.isLoading = false;
            }
          }

          this.isNeedToUpdate = true;

          setTimeout(() => {
             this.closeModal(true);
          }, 2000)
        },
        error: (error) => {
          if (tryToPrint) {
            this.printFailed();
          }
        },
      });
  }

  ngOnDestroy(): void {
    this.closeListener$.next(null);
    this.closeListener$.complete();
  }
}
